import Konva from 'konva';

import {
	getBoxInnerMeshHorizontalOffsets,
	getBoxInnerMeshVerticalOffsets,
} from './extension/mesh';
import { konvaSettings } from '../konvaSettings';

const DEFAULT_FRAME_LINE_WIDTH = 1;
const DEFAULT_INNER_LINE_WIDTH = 1;

export const SLICED_BOX_FRONT_INNER_LINE_WIDTH_PX = 3;

export function appendBoxMeshOpenFrame(
	group: Konva.Group,
	widthPx: number,
	heightPx: number,
	wallWidthPx: number,
	color: string,
	frameLineWidthPx: number = DEFAULT_FRAME_LINE_WIDTH
): void {
	group.add(
		new Konva.Line({
			points: [0, heightPx, widthPx, heightPx],
			stroke: color,
			strokeWidth: frameLineWidthPx,
			name: 'stroke-dark stroke-active'
		})
	);
	group.add(
		new Konva.Line({
			points: [wallWidthPx / 2, 0, wallWidthPx / 2, heightPx],
			stroke: color,
			strokeWidth: wallWidthPx,
			name: 'stroke-dark stroke-active'
		})
	);
	group.add(
		new Konva.Line({
			points: [wallWidthPx / 2, 0, widthPx, 0],
			stroke: color,
			strokeWidth: frameLineWidthPx,
			name: 'stroke-dark stroke-active'
		})
	);
	group.add(
		new Konva.Line({
			points: [widthPx - wallWidthPx / 2, 0, widthPx - wallWidthPx / 2, heightPx],
			stroke: color,
			strokeWidth: wallWidthPx,
			name: 'stroke-dark stroke-active'
		})
	);
	group.add(new Konva.Rect({
		name: 'stroke-legacy-dark stroke-active',
		x: 0,
		y: -1,
		width: widthPx,
		height: heightPx + 2,
		strokeWidth: 1,
		stroke: konvaSettings.strokeColor
	}));
}

export function appendBoxInnerMeshGrid(
	group: Konva.Group,
	boxWidth: number,
	boxHeight: number,
	wallWidth: number,
	scale: number,
	color: string,
	innerLineWidthPx: number = DEFAULT_INNER_LINE_WIDTH,
	displayWidth?: number,
	displayHeight?: number,
	horizontalOffsets?: Array<number>
): void {
	const renderWidth = displayWidth ?? boxWidth;
	const renderHeight = displayHeight ?? boxHeight;
	const widthPx = renderWidth * scale;
	const heightPx = renderHeight * scale;
	const horizontals = horizontalOffsets ?? getBoxInnerMeshHorizontalOffsets(renderHeight);

	getBoxInnerMeshVerticalOffsets(renderWidth, wallWidth).forEach((offset) => {
		const x = offset * scale;

		group.add(
			new Konva.Line({
				points: [x, 0, x, heightPx],
				stroke: color,
				strokeWidth: innerLineWidthPx,
				name: 'stroke-dark stroke-active'
			})
		);
	});

	horizontals.forEach((offset) => {
		const y = (renderHeight - offset) * scale;

		group.add(
			new Konva.Line({
				points: [1, y, widthPx - 2, y],
				stroke: color,
				strokeWidth: innerLineWidthPx,
				name: 'stroke-dark stroke-active'
			})
		);
	});
}

export function appendSlicedMeshVerticalSegment(
	group: Konva.Group,
	x: number,
	yStart: number,
	yEnd: number,
	frontBandTopY: number,
	color: string,
	backLineWidthPx: number,
	frontLineWidthPx: number,
	name: string = 'stroke-dark stroke-active'
): void {
	const yTop = Math.min(yStart, yEnd);
	const yBottom = Math.max(yStart, yEnd);

	if (yBottom <= yTop) {
		return;
	}

	if (yBottom <= frontBandTopY || frontBandTopY <= 0) {
		group.add(
			new Konva.Line({
				points: [x, yTop, x, yBottom],
				stroke: color,
				strokeWidth: frontBandTopY <= 0 ? frontLineWidthPx : backLineWidthPx,
				name
			})
		);

		return;
	}

	if (yTop >= frontBandTopY) {
		group.add(
			new Konva.Line({
				points: [x, yTop, x, yBottom],
				stroke: color,
				strokeWidth: frontLineWidthPx,
				name
			})
		);

		return;
	}

	group.add(
		new Konva.Line({
			points: [x, yTop, x, frontBandTopY],
			stroke: color,
			strokeWidth: backLineWidthPx,
			name
		})
	);
	group.add(
		new Konva.Line({
			points: [x, frontBandTopY, x, yBottom],
			stroke: color,
			strokeWidth: frontLineWidthPx,
			name
		})
	);
}

export function appendSlicedBoxInnerMeshGrid(
	group: Konva.Group,
	boxWidth: number,
	frontHeight: number,
	backHeight: number,
	wallWidth: number,
	scale: number,
	color: string,
	innerLineWidthPx: number = DEFAULT_INNER_LINE_WIDTH,
	frontInnerLineWidthPx: number = SLICED_BOX_FRONT_INNER_LINE_WIDTH_PX,
	displayWidth?: number,
	displayHeight?: number
): void {
	const intrinsicHeight = Math.max(frontHeight, backHeight);
	const renderWidth = displayWidth ?? boxWidth;
	const renderHeight = displayHeight ?? intrinsicHeight;
	const widthPx = renderWidth * scale;
	const heightPx = renderHeight * scale;
	const frontBandTopY = Math.min(Math.max((renderHeight - frontHeight) * scale, 0), heightPx);

	getBoxInnerMeshVerticalOffsets(renderWidth, wallWidth).forEach((offset) => {
		appendSlicedMeshVerticalSegment(
			group,
			offset * scale,
			0,
			heightPx,
			frontBandTopY,
			color,
			innerLineWidthPx,
			frontInnerLineWidthPx
		);
	});

	const horizontalOffsetsBack = getBoxInnerMeshHorizontalOffsets(backHeight);
	const horizontalOffsetsFront = getBoxInnerMeshHorizontalOffsets(frontHeight);

	horizontalOffsetsBack.forEach((offset) => {
		const y = (renderHeight - offset) * scale;
	
		group.add(
			new Konva.Line({
				points: [1, y, widthPx - 1, y],
				stroke: color,
				strokeWidth: innerLineWidthPx,
				name: 'stroke-dark stroke-active'
			})
		);
	});

	horizontalOffsetsFront.forEach((offset, index, { length }) => {
		const y = (renderHeight - offset) * scale;

		group.add(
			new Konva.Line({
				points: [1, y, widthPx - 1, y],
				stroke: color,
				strokeWidth: frontInnerLineWidthPx,
				name: 'stroke-dark stroke-active'
			})
		);

		if ( index === length - 1 ) {
			group.add(
				new Konva.Line({
					points: [0, y - frontInnerLineWidthPx / 2, widthPx, y - frontInnerLineWidthPx / 2],
					stroke: konvaSettings.strokeColor,
					strokeWidth: 1,
					name: 'stroke-legacy-dark stroke-active'
				})
			);
		}
	});
}

/** Side face: span along first axis (e.g. depth), height along second. */
export function appendBoxMeshSideFaceGrid(
	group: Konva.Group,
	spanLength: number,
	height: number,
	wallWidth: number,
	unit: number,
	color: string,
	grid: boolean,
	innerLineWidthPx: number = DEFAULT_INNER_LINE_WIDTH
): void {
	const widthPx = spanLength * unit;
	const heightPx = height * unit;
	const wallWidthPx = wallWidth * unit;

	appendBoxMeshOpenFrame(group, widthPx, heightPx, wallWidthPx, color);

	getBoxInnerMeshVerticalOffsets(spanLength, wallWidth).forEach((offset) => {
		const x = offset * unit;

		group.add(
			new Konva.Line({
				points: [x, 0, x, heightPx],
				stroke: color,
				strokeWidth: innerLineWidthPx
			})
		);
	});

	if (grid) {
		getBoxInnerMeshHorizontalOffsets(height).forEach((offset) => {
			const y = (height - offset) * unit;

			group.add(
				new Konva.Line({
					points: [0, y, widthPx, y],
					stroke: color,
					strokeWidth: innerLineWidthPx
				})
			);
		});
	} else {
		const bar = Math.min(wallWidth, spanLength, height) * unit;

		group.add(
			new Konva.Line({
				points: [0, 0, widthPx, 0],
				stroke: color,
				strokeWidth: bar
			}),
			new Konva.Line({
				points: [0, heightPx - bar, widthPx, heightPx - bar],
				stroke: color,
				strokeWidth: bar
			})
		);
	}
}
